import { Public_Routes } from '@common/url';
import { signup } from '@common/user-analytics';
import { ArrowRightIcon } from '@prophecy/icons';
import { EventName, EventType, metrics } from '@prophecy/metrics';
import { theme } from '@prophecy/theme';
import { Button, Display, Input, Stack, Text } from '@prophecy/ui-v3';
import { validateEmail } from '@prophecy/utils/validation';
import { useMutation } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useMixPanel } from '../../context/mixpanel/context';
import { forgotPassword } from '../../data/apis/api';
import { errorHandler } from '../../data/util';
import { StyledForm, StyledFormContainer } from './common';
import { OtpForm } from './signup/OtpForm';
import { PasswordForm } from './signup/Password';

type StepType = 'reset' | 'otp' | 'password';

function ResetPasswordForm({ gotoNextStep }: { gotoNextStep: (params: { email: string }) => void }) {
  const { track } = useMixPanel();
  const {
    handleSubmit,
    control,
    formState: { errors }
  } = useForm({ mode: 'onChange', defaultValues: { email: '' } });
  const { isLoading, mutateAsync: doForgotPassword } = useMutation(forgotPassword, { onError: errorHandler });
  async function onSubmit({ email }: { email: string }) {
    track(signup.normal.resetPasswordSubmit, { email });
    metrics.track({
      eventName: EventName.ui.page.resetPassword.ResetPasswordButtonClicked,
      eventType: EventType.userAction
    });
    await doForgotPassword({
      identifier: email
    });
    gotoNextStep({ email });
  }

  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect') || undefined;

  return (
    <Stack height='100%' width='100%' align='center' alignY='center'>
      <StyledFormContainer>
        <Stack alignY='center' gap={theme.spaces.x32} height='100%' align='center'>
          <Stack alignY='center' gap={theme.spaces.x12}>
            <Display level='sm' weight={theme.fontWeight.semiBold}>
              Reset Your Password
            </Display>
            <Text elementType='p' level='sm13' color={theme.colors.gray500}>
              Forgot your password? Please enter your email address. You will receive a otp to create a new password via
              email.
            </Text>
          </Stack>
          <StyledForm onSubmit={handleSubmit(onSubmit)} method='post'>
            <Stack gap={theme.spaces.x24} width='100%' alignY='stretch'>
              <Controller
                render={({ field }) => (
                  <Input
                    type='text'
                    placeholder='Enter your email'
                    {...field}
                    variant={errors.email ? 'error' : 'primary'}
                    helpText={errors.email?.message}
                    inputSize='l'
                    data-test-id='reset-password-email-input'
                  />
                )}
                control={control}
                name='email'
                rules={{
                  required: 'Email is required',
                  validate: validateEmail
                }}
              />
              <Button type='submit' size='l' loading={isLoading} data-test-id='reset-password-reset-password-button'>
                Reset Password
              </Button>
            </Stack>
          </StyledForm>
          <Button
            variant='plain'
            elementType='link'
            to={Public_Routes.SignIn.getUrlWithBasePath(undefined, { redirect })}
            data-test-id='reset-password-back-to-login-button'>
            <Stack gap={theme.spaces.x12} direction='horizontal'>
              <ArrowRightIcon type='default' color={theme.colors.gray500} />
              <Text level='sm' tone={theme.colors.gray500}>
                Back to log in
              </Text>
            </Stack>
          </Button>
        </Stack>
      </StyledFormContainer>
    </Stack>
  );
}

export default function Page() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const email = params.get('email') || '';
  const redirect = params.get('redirect') || undefined;
  const step: StepType = (params.get('step') as StepType) || 'reset';
  const company = params.get('company') || undefined;

  const gotoNextStep = (step: StepType, params: { email: string }) => {
    navigate(Public_Routes.ResetPassword.getUrlWithBasePath(undefined, { ...params, step, redirect }), {
      replace: true
    });
  };

  switch (step) {
    case 'reset': {
      return <ResetPasswordForm gotoNextStep={(params) => gotoNextStep('otp', params)} />;
    }
    case 'otp': {
      return <OtpForm email={email} gotoNextStep={(params) => gotoNextStep('password', params)} />;
    }
    case 'password': {
      return <PasswordForm email={email} company={company} preserveSession />;
    }
  }
}
